import { getApiBaseUrl } from "../config/api";
import { authService } from "../services/auth";

export interface ImpressionFinding {
  id: string;
  section: string;
  text: string;
  seriesId: string | null;
  sliceIndex: number | null;
}

export interface ImpressionItem {
  id: string;
  text: string;
  citedFindings: string[];
}

export interface StreamImpressionOptions {
  signal?: AbortSignal;
  /** The study these findings belong to; the server adds its imaged regions. */
  studyId?: string | null;
  onItem?: (item: ImpressionItem) => void;
}

type ImpressionEvent =
  | ({ type: "item" } & ImpressionItem)
  | { type: "complete" }
  | { type: "error"; message?: string }
  | { type: "progress"; stage?: string };

/**
 * The one browser client for GPT Impression generation. It validates the
 * streamed contract and resolves only after the backend explicitly completes,
 * so callers cannot mistake a truncated or failed stream for a usable report.
 */
export async function streamImpression(
  findings: ReadonlyArray<ImpressionFinding>,
  options: StreamImpressionOptions = {},
): Promise<ImpressionItem[]> {
  if (findings.length === 0) throw new Error("No findings provided for Impression generation.");

  const { token } = authService.getStoredAuth();
  if (!token) throw new Error("Sign in again to generate an Impression.");

  const validIds = new Set(findings.map((finding) => finding.id));
  const response = await fetch(`${getApiBaseUrl()}/api/stream_impression`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      context: { scope: "current_exam_only" },
      findings,
      ...(options.studyId ? { study_id: options.studyId } : {}),
    }),
    signal: options.signal,
  });
  if (!response.ok || !response.body) {
    throw new Error(`Impression generation failed (HTTP ${response.status}).`);
  }

  const items: ImpressionItem[] = [];
  let complete = false;

  const processLine = (line: string): void => {
    if (!line.startsWith("data: ")) return;
    let event: ImpressionEvent;
    try {
      event = JSON.parse(line.slice(6)) as ImpressionEvent;
    } catch {
      throw new Error("Impression generation returned malformed data.");
    }

    if (event.type === "error") {
      throw new Error(event.message?.trim() || "Impression generation failed.");
    }
    if (event.type === "complete") {
      complete = true;
      return;
    }
    if (event.type !== "item") return;

    const citedFindings = Array.isArray(event.citedFindings)
      ? event.citedFindings.filter((id) => validIds.has(id))
      : [];
    if (!event.id?.trim() || !event.text?.trim() || citedFindings.length === 0) {
      throw new Error("Impression generation returned an ungrounded item.");
    }
    const item = {
      id: event.id,
      text: event.text.trim(),
      citedFindings,
    };
    items.push(item);
    options.onItem?.(item);
  };

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) processLine(line.trimEnd());
  }
  buffer += decoder.decode();
  if (buffer.trim()) processLine(buffer.trim());

  if (!complete) throw new Error("Impression generation ended before completion.");
  if (items.length === 0) throw new Error("Impression generation produced no items.");
  return items;
}
